import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Check } from "lucide-react";
import { IMAGES } from "../../data/content";
import { useLang } from "../../i18n/LanguageContext";

const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Label = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-[#A9CE8A] text-xs font-semibold uppercase tracking-[0.25em] mb-4">
    <span className="w-8 h-px bg-[#A9CE8A]" /> {children}
  </span>
);

const glass = "backdrop-blur-md bg-black/35 border border-white/10 rounded-2xl";
const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative min-h-screen flex items-center py-28 ${className}`}>
    <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">{children}</div>
  </section>
);

export const Hero = () => {
  const { t } = useLang();
  return (
    <section id="top" className="relative h-screen flex flex-col items-center justify-center text-center px-6">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
        <span className="text-[#A9CE8A] tracking-[0.3em] uppercase text-xs md:text-sm">{t.hero.eyebrow}</span>
        <h1 className="mt-5 font-display font-extrabold text-[#F6F5F0] text-5xl sm:text-6xl md:text-8xl leading-[0.95] max-w-5xl">
          {t.hero.title1}<br /><span className="text-[#A9CE8A]">{t.hero.title2}</span>
        </h1>
        <p className="mt-6 mx-auto max-w-xl text-[#ECE4D6]/90 text-base md:text-lg">{t.hero.subtitle}</p>
      </motion.div>
      <div className="absolute bottom-10 flex flex-col items-center gap-2 text-[#F6F5F0]/70 text-xs uppercase tracking-widest">
        {t.hero.scroll} <ArrowDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
};

const Intro = () => {
  const { t } = useLang(); const c = t.intro;
  return (
    <Section id="intro">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5"><Reveal><Label>{c.label}</Label>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#F6F5F0] leading-[1.05]">{c.heading}</h2>
        </Reveal></div>
        <Reveal delay={0.1} className={`md:col-span-7 p-8 ${glass}`}>
          <p className="text-[#ECE4D6]/90 text-lg leading-relaxed">{c.p1}</p>
          <p className="mt-5 text-[#ECE4D6]/90 text-lg leading-relaxed">{c.p2a}<span className="text-[#A9CE8A] font-semibold">{c.p2b}</span>{c.p2c}<span className="text-[#A9CE8A] font-semibold">{c.p2d}</span>{c.p2e}</p>
          <p className="mt-6 font-display text-2xl font-bold text-[#A9CE8A]">{c.tagline}</p>
        </Reveal>
      </div>
    </Section>
  );
};

const DivisionSlide = ({ scrollYProgress, i, total, d }) => {
  const seg = 1 / total;
  const center = (i + 0.5) * seg;
  const inStart = i === 0 ? 0 : center - seg;
  const outEnd = i === total - 1 ? 2 : center + seg;
  const opacity = useTransform(scrollYProgress, [inStart, center, outEnd], [i === 0 ? 1 : 0, 1, i === total - 1 ? 1 : 0]);
  const y = useTransform(scrollYProgress, [inStart, center, outEnd], [50, 0, -50]);
  const scale = useTransform(scrollYProgress, [inStart, center], [0.96, 1]);
  return (
    <motion.div style={{ opacity }} className="absolute inset-0 flex items-center">
      <div className="max-w-6xl mx-auto px-6 md:px-12 w-full grid md:grid-cols-2 gap-8 items-center">
        <motion.div style={{ scale }} className="rounded-2xl overflow-hidden aspect-[4/3] border border-white/10 hidden md:block">
          <img src={IMAGES[d.key]} alt={d.title} className="w-full h-full object-cover" />
        </motion.div>
        <motion.div style={{ y }} className={`p-8 md:p-10 ${glass}`}>
          <div className="flex items-center gap-4">
            <span className="font-display font-extrabold text-[#A9CE8A] text-5xl md:text-6xl">{d.tag.split(" ")[0]}</span>
            <span className="text-[#A9CE8A] text-xs font-semibold uppercase tracking-widest">{d.tag}</span>
          </div>
          <h3 className="mt-3 font-display font-bold text-3xl md:text-4xl text-[#F6F5F0]">{d.title}</h3>
          <p className="mt-2 text-[#A9CE8A] font-medium">{d.subtitle}</p>
          <p className="mt-3 text-[#ECE4D6]/75 text-sm leading-relaxed">{d.desc}</p>
          <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2">
            {d.items.slice(0, 6).map((it) => (
              <li key={it} className="flex items-center gap-2 text-[#ECE4D6]/85 text-sm"><Check className="w-3.5 h-3.5 text-[#69A544] shrink-0" /> {it}</li>
            ))}
          </ul>
        </motion.div>
      </div>
    </motion.div>
  );
};

const TickerBar = ({ scrollYProgress, i, total }) => {
  const seg = 1 / total;
  const w = useTransform(scrollYProgress, [i * seg, (i + 1) * seg], ["0%", "100%"]);
  return (
    <div className="relative h-1 flex-1 rounded-full bg-white/20 overflow-hidden">
      <motion.div style={{ width: w }} className="absolute inset-y-0 left-0 bg-[#A9CE8A]" />
    </div>
  );
};

const Divisions = () => {
  const { t } = useLang();
  const c = t.divisions;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  return (
    <section id="supply" ref={ref} className="relative h-[420vh]">
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col">
        <div className="pt-24 md:pt-28 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <Label>{c.label}</Label>
          <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl text-[#F6F5F0] leading-[1.05] max-w-3xl">
            {c.heading1} <span className="text-[#A9CE8A]">{c.heading2}</span>
          </h2>
        </div>
        <div className="relative flex-1">
          {c.items.map((d, i) => (
            <DivisionSlide key={d.key} scrollYProgress={scrollYProgress} i={i} total={c.items.length} d={d} />
          ))}
        </div>
        <div className="pb-10 max-w-6xl mx-auto px-6 md:px-12 w-full flex items-center gap-3">
          {c.items.map((d, i) => <TickerBar key={i} scrollYProgress={scrollYProgress} i={i} total={c.items.length} />)}
        </div>
      </div>
    </section>
  );
};

const Featured = () => {
  const { t } = useLang(); const c = t.featured;
  return (
    <Section id="products">
      <Reveal><Label>{c.label}</Label>
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#F6F5F0] max-w-2xl leading-[1.05]">{c.heading}</h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        {c.items.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.1} className={`overflow-hidden flex flex-col sm:flex-row ${glass} ${i === c.items.length - 1 && c.items.length % 2 === 1 ? "md:col-span-2" : ""}`}>
            <div className="sm:w-2/5 h-48 sm:h-auto"><img src={IMAGES[p.img]} alt={p.name} className="w-full h-full object-cover" /></div>
            <div className="p-6 sm:w-3/5">
              <span className="text-[#A9CE8A] text-xs font-semibold uppercase tracking-wide">{p.badge}</span>
              <h3 className="mt-1 font-display font-bold text-xl text-[#F6F5F0]">{p.name}</h3>
              <p className="mt-2 text-[#ECE4D6]/75 text-sm">{p.desc}</p>
              <a href="/shop" className="mt-4 inline-flex items-center gap-2 text-[#A9CE8A] font-semibold text-sm">{c.cta} <ArrowRight className="w-4 h-4" /></a>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
};

const Why = () => {
  const { t } = useLang(); const c = t.why;
  return (
    <Section id="why">
      <Reveal><Label>{c.label}</Label>
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#F6F5F0] leading-[1.05] max-w-2xl">{c.heading}</h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {c.items.map((w, i) => (
          <Reveal key={w.title} delay={(i % 3) * 0.08} className={`p-7 ${glass}`}>
            <span className="font-display text-[#A9CE8A] text-3xl font-extrabold">0{i + 1}</span>
            <h3 className="mt-3 font-display font-bold text-xl text-[#F6F5F0]">{w.title}</h3>
            <p className="mt-2 text-[#ECE4D6]/75 text-sm leading-relaxed">{w.desc}</p>
          </Reveal>
        ))}
      </div>
    </Section>
  );
};

const Wholesale = () => {
  const { t } = useLang(); const c = t.wholesale;
  return (
    <Section id="wholesale">
      <Reveal className={`p-9 md:p-12 ${glass} max-w-4xl`}>
        <Label>{c.label}</Label>
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#F6F5F0] leading-[1.05]">{c.heading}</h2>
        <p className="mt-5 text-[#ECE4D6]/85 text-lg">{c.p}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {c.badges.map((b) => <span key={b} className="text-sm font-medium text-[#F6F5F0] border border-white/20 px-3.5 py-1.5 rounded-full">{b}</span>)}
        </div>
        <a href="#contact" className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#69A544] text-[#0E2A17] font-semibold hover:bg-[#A9CE8A] transition-colors">{c.cta} <ArrowRight className="w-4 h-4" /></a>
      </Reveal>
    </Section>
  );
};

const PrivateLabel = () => {
  const { t } = useLang(); const c = t.privatelabel;
  return (
    <Section id="privatelabel">
      <div className="grid md:grid-cols-12 gap-8 items-center">
        <Reveal className={`md:col-span-7 p-9 ${glass}`}>
          <Label>{c.label}</Label>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#F6F5F0] leading-[1.05]">{c.heading}</h2>
          <p className="mt-5 text-[#ECE4D6]/85 text-lg">{c.p}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {c.cats.map((x) => <span key={x} className="text-sm font-medium text-[#F6F5F0] border border-white/20 px-3.5 py-1.5 rounded-full">{x}</span>)}
          </div>
          <a href="#contact" className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#F6F5F0] text-[#3A2718] font-semibold">{c.cta} <ArrowRight className="w-4 h-4" /></a>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-5"><img src={IMAGES.retail} alt="Private label" className="rounded-2xl w-full aspect-square object-cover" /></Reveal>
      </div>
    </Section>
  );
};

const Sustainability = () => {
  const { t } = useLang(); const c = t.sustainability;
  return (
    <Section id="sustainability">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5"><Reveal><Label>{c.label}</Label>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#F6F5F0] leading-[1.05]">{c.heading}</h2>
          <p className="mt-5 text-[#ECE4D6]/85 text-lg">{c.p}</p>
        </Reveal></div>
        <Reveal delay={0.1} className={`md:col-span-7 p-8 ${glass}`}>
          <ul className="divide-y divide-white/10">
            {c.items.map((s, i) => (
              <li key={s} className="flex items-center gap-5 py-4">
                <span className="font-display text-[#A9CE8A] text-lg w-8">0{i + 1}</span>
                <span className="text-[#F6F5F0] text-lg font-medium">{s}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </Section>
  );
};

const Compliance = () => {
  const { t } = useLang(); const c = t.compliance;
  return (
    <Section id="compliance">
      <Reveal className="max-w-2xl"><Label>{c.label}</Label>
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#F6F5F0] leading-[1.05]">{c.heading}</h2>
        <p className="mt-5 text-[#ECE4D6]/85 text-lg">{c.p}</p>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {c.items.map((x, i) => (
          <Reveal key={x} delay={(i % 4) * 0.06} className={`p-5 ${glass} text-[#ECE4D6] font-medium`}>{x}</Reveal>
        ))}
      </div>
    </Section>
  );
};

export const JourneyContent = () => (
  <>
    <Hero />
    <Intro />
    <Divisions />
    <Featured />
    <Why />
    <Wholesale />
    <PrivateLabel />
    <Sustainability />
    <Compliance />
  </>
);
